/*
Copyright 2022 Alexandra Instituttet

Der er givet en begrænset licens af denne software til GEO. Licensen
giver dem ret til at benytte, videreudvikle og fejlrette udelukkende
med henblik på brug i GeoAtlasStudy web-applikationen.
*/
import { vec2 } from "gl-matrix";
import { WMath } from "wfundament";

export class ClipPolygon {
  constructor() {
    this.polygon_ = []; // vec2d array
    this.polygonNormals_ = [];
    this.isValid_ = false;
    this.isClosed_ = false;
    this.closePolygonRadius_ = 3.0;
    this.isClockwize_ = true;
  }

  clear() {
    this.polygon_.length = 0;
    this.polygonNormals_.length = 0;
    this.isValid_ = false;
    this.isClosed_ = false;
  }

  isValid() {
    return this.isValid_;
  }

  isClosed() {
    return this.isClosed_;
  }

  setClosePolygonRadius(radius) {
    this.closePolygonRadius_ = radius;
  }

  getClosePolygonRadius() {
    return this.closePolygonRadius_;
  }

  addPoint(point) {
    // test for closing
    if (this.getNumberOfPoints() > 1) {
      const distToInitialPoint = vec2.length(
        WMath.vec2Sub(this.getPoint(0), point)
      );
      if (distToInitialPoint < this.getClosePolygonRadius()) {
        this.closePolygon();
        return;
      }
    }

    this.polygon_.push(point);
    if (this.getNumberOfPoints() > 1) {
      const edge = WMath.vec2Sub(
        point,
        this.getPoint(this.getNumberOfPoints() - 2)
      );
      let edgeNormal = WMath.vec2GetOrthogonalNegative(edge);
      this.polygonNormals_.push(WMath.vec2Normalize(edgeNormal));
    }

    this.isValid_ = this.validateClipPolygon(); // test for selfintersections

    if (!this.isValid_) {
      this.clear();
      return false;
    }
    return true;
  }

  isPointInsideClosingCircle(point) {
    if (this.getNumberOfPoints() === 0) {
      return false;
    }
    const distToInitialPoint = vec2.length(
      WMath.vec2Sub(this.getPoint(0), point)
    );
    return distToInitialPoint < this.getClosePolygonRadius();
  }

  closePolygon() {
    this.polygon_.push(this.getPoint(0));
    const edge = WMath.vec2Sub(
      this.getPoint(0),
      this.getPoint(this.getNumberOfPoints() - 2)
    );
    let edgeNormal = WMath.vec2GetOrthogonalNegative(edge);
    this.polygonNormals_.push(WMath.vec2Normalize(edgeNormal));
    this.isClosed_ = true;
    let signedArea = this.calculateSignedArea();
    this.isClockwize_ = signedArea >= 0.0;

    if (this.isClockwize_) {
      console.log("polygon is clockwize");
    } else {
      console.log("polygon is counter clockwize");
      this.changeWinding();
    }
    return true;
  }

  changeWinding() {
    let tmpPoly = [];
    let tmpPolyNormals = [];

    for (let i = 0; i < this.polygon_.length; i++) {
      var p = vec2.create();
      vec2.copy(p, this.polygon_[i]);
      tmpPoly.push(p);
    }

    for (let i = 0; i < this.polygonNormals_.length; i++) {
      var n = WMath.vec2Scale(this.polygonNormals_[i], -1.0);
      tmpPolyNormals.push(n);
    }

    this.polygon_.length = 0;
    this.polygonNormals_.length = 0;

    for (let i = tmpPoly.length - 1; i >= 0; i--) {
      let p = vec2.create();
      vec2.copy(p, tmpPoly[i]);
      this.polygon_.push(p);
    }

    for (let i = tmpPolyNormals.length - 1; i >= 0; i--) {
      this.polygonNormals_.push(tmpPolyNormals[i]);
    }
  }

  calculateSignedArea() {
    var signedArea = 0.0;
    for (let i = 0; i < this.getNumberOfPoints() - 1; i++) {
      let p0 = this.getPoint(i);
      let p1 = this.getPoint(i + 1);
      signedArea += p0[0] * p1[1] - p0[1] * p1[0];
    }
    return signedArea * 0.5;
  }

  getNumberOfPoints() {
    return this.polygon_.length;
  }

  getPoint(index) {
    let p = vec2.create();
    if (index > this.getNumberOfPoints()) {
      return p;
    }
    vec2.copy(p, this.polygon_[index]);
    return p;
  }

  getNormal(index) {
    let n = vec2.create();
    if (index > this.getNumberOfPoints() - 1) {
      return n;
    }
    vec2.copy(n, this.polygonNormals_[index]);
    return n;
  }

  isPointInside(point) {
    // let windingNumber = WMath.calculateWindingNumber2D(point, this.polygon_);
    let windingNumber = WMath.getWindingNumber(point, this.polygon_);
    return windingNumber === 1; // (windingNumber % 2 == 1);
  }

  validateClipPolygon() {
    if (this.polygon_.length < 4) {
      return true;
    } // less than two lines

    for (let i = 0; i < this.polygon_.length - 1; i++) {
      let v0 = this.polygon_[i];
      let v1 = this.polygon_[i + 1];

      for (let j = i + 1; j < this.polygon_.length - 1; j++) {
        let v2 = this.polygon_[j];
        let v3 = this.polygon_[j + 1];

        let t = WMath.lineLineIntersection(
          v0[0],
          v0[1],
          v1[0],
          v1[1],
          v2[0],
          v2[1],
          v3[0],
          v3[1]
        );

        if (t > 0.0) {
          return false;
        } // there is an intersection
      }
    }
    return true;
  }

  pointInsideBox(point, boxVertices) {
    // let windingNumber = WMath.calculateWindingNumber2D(point, boxVertices);
    // return (windingNumber % 2 == 1);
    let windingNumber = WMath.getWindingNumber(point, boxVertices);
    return windingNumber === 1; // (windingNumber % 2 == 1);
  }

  findBoxPolygonIntersectionSegments(boxVertices, polyEdges) {
    let numPolyEdges = 0;
    for (let i = 0; i < this.getNumberOfPoints() - 1; i++) {
      const p0 = this.polygon_[i];
      const p1 = this.polygon_[i + 1];
      let edgeIntersection = false;

      // test if any of the points are contained
      if (this.pointInsideBox(p0, boxVertices)) {
        edgeIntersection = true;
      } else if (this.pointInsideBox(p1, boxVertices)) {
        edgeIntersection = true;
      } else {
        // line/line intersection
        for (let j = 0; j < 4; j++) {
          const b0 = boxVertices[j];
          const b1 = boxVertices[(j + 1) % 4];
          let t = WMath.lineLineIntersection(
            p0[0],
            p0[1],
            p1[0],
            p1[1],
            b0[0],
            b0[1],
            b1[0],
            b1[1]
          );

          if (t >= 0.0) {
            edgeIntersection = true;
          }
        }
      }

      if (edgeIntersection) {
        polyEdges[numPolyEdges * 4] = p0[0];
        polyEdges[numPolyEdges * 4 + 1] = p0[1];
        polyEdges[numPolyEdges * 4 + 2] = p1[0];
        polyEdges[numPolyEdges * 4 + 3] = p1[1];
        numPolyEdges++;
      }
    }
    return numPolyEdges;
  }

  boxPolygonIntersection(boxVertices) {
    if (boxVertices.length !== 4) {
      return false;
    }

    for (let i = 0; i < 4; i++) {
      let b0 = boxVertices[i];
      let b1 = boxVertices[(i + 1) % 4];

      if (this.linePolygonIntersection(b0, b1)) {
        return true;
      }
    }
    return false;
  }

  linePolygonIntersection(v0, v1) {
    for (let i = 0; i < this.getNumberOfPoints() - 1; i++) {
      let p0 = this.polygon_[i];
      // let p1 = this.polygon_[(i + 1) % this.getNumberOfPoints()];
      let p1 = this.polygon_[i + 1];

      let t = WMath.lineLineIntersection(
        v0[0],
        v0[1],
        v1[0],
        v1[1],
        p0[0],
        p0[1],
        p1[0],
        p1[1]
      );

      if (t >= 0.0) {
        return true;
      }
    }
    return false;
  }
}
